import {
  FC,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
  createContext,
} from "react";
import { ExerciseCorrectionItem, ExerciseOptionTypes } from "@/types";

interface State {
  startTime?: number;
  exerciseId: number;
  viewResults: boolean;
  currentIndex: number;
  timerDuration?: number;
  remainingTime?: number;
  totalExercises: number;
  checkpointTime: number | undefined;
  exerciseResults: Array<{
    type: ExerciseOptionTypes;
    score: number;
    total: number;
  }>;
  isExerciseStarted: boolean;
  currentExerciseCompleted: boolean;
}

const initialState: State = {
  exerciseId: 0,
  viewResults: false,
  currentIndex: 0,
  checkpointTime: 0,
  totalExercises: 0,
  exerciseResults: [],
  isExerciseStarted: false,
  currentExerciseCompleted: false,
};

type Action =
  | { type: "setExerciseId"; payload: number }
  | { type: "setTotalExercises"; payload: number }
  | {
      type: "startSequence";
      payload: {
        startTime: number;
        timerDuration: number;
        newExerciseIndex: number;
      };
    }
  | { type: "nextExercise" }
  | { type: "endSequence" }
  | { type: "completeCurrentExercise" }
  | { type: "saveRemainingTime"; callback?: () => void }
  | { type: "resetTimerToCheckpoint" }
  | { type: "setTimer"; payload: number }
  | {
      type: "updateExerciseResult";
      payload: ExerciseCorrectionItem[] | ExerciseCorrectionItem;
    }
  | { type: "setViewResults"; value: boolean };

function exerciseReducer(state: State, action: Action): State {
  switch (action.type) {
    case "setExerciseId":
      return {
        ...state,
        exerciseId: action.payload,
      };
    case "startSequence":
      return {
        ...state,
        startTime: action.payload.startTime,
        currentIndex: action.payload.newExerciseIndex,
        timerDuration: action.payload.timerDuration,
        isExerciseStarted: true,
      };
    case "nextExercise": {
      const nextIndex = state.currentIndex + 1;

      // Don't proceed if we're at the last exercise
      if (nextIndex >= state.totalExercises) {
        return state;
      }

      return {
        ...state,
        currentIndex: nextIndex,
        isExerciseStarted: true,
        currentExerciseCompleted: false,
      };
    }
    case "completeCurrentExercise":
      return {
        ...state,
        currentIndex: state.currentIndex + 1,
        currentExerciseCompleted: true,
      };
    case "endSequence":
      return {
        ...initialState,
      };
    case "setTotalExercises":
      if (state.totalExercises === action.payload || action.payload <= 0) {
        return state;
      }
      return {
        ...state,
        totalExercises: action.payload,
      };
    case "saveRemainingTime": {
      const currentTime = Date.now();
      const elapsedSeconds = (currentTime - state.startTime!) / 1000;
      const remainingSeconds = state.timerDuration! - elapsedSeconds;
      const remainingTime = Math.max(Math.floor(remainingSeconds), 0);

      // Chiama la callback solo se definita e solo se il tempo è cambiato
      if (action.callback && remainingTime !== state.remainingTime) {
        action.callback();
      }

      return {
        ...state,
        remainingTime,
      };
    }
    case "resetTimerToCheckpoint":
      return {
        ...state,
        remainingTime: state.checkpointTime,
      };
    case "setTimer":
      return {
        ...state,
        remainingTime: action.payload,
      };
    case "updateExerciseResult": {
      const newResults = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      const updatedResults = state.exerciseResults.slice();

      newResults.forEach((newResult) => {
        const index = updatedResults.findIndex(
          (result) => result.type === newResult.type,
        );
        if (index !== -1) {
          updatedResults[index] = newResult;
        } else {
          updatedResults.push(newResult);
        }
      });

      return {
        ...state,
        exerciseResults: updatedResults,
      };
    }
    case "setViewResults":
      return {
        ...state,
        viewResults: action.value,
      };
    default:
      return state;
  }
}

const ExerciseStateContext = createContext<State>(initialState);
const ExerciseActionContext = createContext<Dispatch<Action> | undefined>(
  undefined,
);

export const ExerciseProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(exerciseReducer, initialState);

  return (
    <ExerciseStateContext.Provider value={state}>
      <ExerciseActionContext.Provider value={dispatch}>
        {children}
      </ExerciseActionContext.Provider>
    </ExerciseStateContext.Provider>
  );
};

/* eslint-disable react-refresh/only-export-components */
export function useExerciseState() {
  const context = useContext(ExerciseStateContext);

  if (context === undefined) {
    throw new Error("useExerciseState must be used within an ExerciseProvider");
  }

  return context;
}

/* eslint-disable react-refresh/only-export-components */
export function useExerciseAction() {
  const dispatch = useContext(ExerciseActionContext);

  if (dispatch === undefined) {
    throw new Error(
      "useExerciseAction must be used within an ExerciseProvider",
    );
  }

  return {
    setExerciseId: (id: number) => {
      dispatch({ type: "setExerciseId", payload: id });
    },
    startSequence: (
      startTime: number,
      timerDuration: number,
      newExerciseIndex: number = 0,
    ) => {
      dispatch({
        type: "startSequence",
        payload: { startTime, timerDuration, newExerciseIndex },
      });
    },
    nextExercise: () => {
      dispatch({ type: "nextExercise" });
    },
    endSequence: () => {
      dispatch({ type: "endSequence" });
    },
    completeCurrentExercise: () => {
      dispatch({ type: "completeCurrentExercise" });
    },
    saveRemainingTime: (callback: () => void) => {
      dispatch({ type: "saveRemainingTime", callback });
    },
    resetTimerToCheckpoint: () => {
      dispatch({ type: "resetTimerToCheckpoint" });
    },
    setTimer: (time: number) => {
      dispatch({ type: "setTimer", payload: time });
    },
    updateExerciseResult: (
      result: ExerciseCorrectionItem[] | ExerciseCorrectionItem,
    ) => {
      dispatch({ type: "updateExerciseResult", payload: result });
    },
    setTotalExercises: (total: number) => {
      dispatch({ type: "setTotalExercises", payload: total });
    },
    setViewResults: (value: boolean) => {
      dispatch({ type: "setViewResults", value });
    },
  };
}
